import React, { useState, useRef, useEffect } from 'react';
import { Button } from "react-bootstrap";
import { FaBold, FaItalic, FaUnderline, FaTimes, FaPaperclip, FaSave, FaSuperscript, FaSubscript, FaRegTrashAlt, FaChevronDown } from "react-icons/fa";
import pathconfig from '../../../pathconfig.json';
import axios from "axios";
import { toastError, toastSuccess } from '../../toaster';
import packjson from '../../../../package.json';
import { useAppContextProvider } from '../../../contextapi';

// Media and Image attachments must each be one of these file types.
const ALLOWED_MEDIA_EXTENSIONS = ['ttml', 'txt', 'vtt', 'mp4', 'mp3', 'gif'];
const ALLOWED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf', 'tif', 'tiff', 'eps', 'svg'];
const getFileExtension = (filename) => (filename.split('.').pop() || '').toLowerCase();
const isValidMediaFile = (filename) => ALLOWED_MEDIA_EXTENSIONS.includes(getFileExtension(filename));
const isValidImageFile = (filename) => ALLOWED_IMAGE_EXTENSIONS.includes(getFileExtension(filename));

// Display label for an Image tab file, mirroring the Image tab's own modal
// title (getImageTitle in imagelist.js) — e.g. "Fig. 4 (gr4.pdf)",
// "Inline Image (fx1.pdf)" — so the "Select Image to Replace" list matches
// what the Master Copier actually sees when they open that image.
const IMAGE_TYPE_MAP = {
    gr: "Graphical Image",
    ga: "Graphical Abstract Image",
    fx: "Inline Image",
    mmc: "Supplementary videos and gifs",
};
const getImageDisplayTitle = (name) => {
    if (!name) return "";
    const baseName = name.split(".")[0];
    const match = baseName.match(/^([a-zA-Z]+)(\d+)$/);
    const prefix = match?.[1]?.toLowerCase();
    const number = match?.[2];
    if (prefix === "gr") return `Fig. ${number} (${name})`;
    if (prefix === "sc") return `Scheme ${number}`;
    if (prefix === "pl") return `Plate ${number}`;
    const fullName = IMAGE_TYPE_MAP[prefix] || "";
    return fullName ? `${fullName} (${name})` : name;
};

const CommentBox = ({ onSaveComment, onCancelComment, comments = [], getTriggerfile, initialContent, commentAttach, fileProgress, refreshAttachments, isReply, showCategory, initialCategory }) => {
    // const [comment, setComment] = useState([]);
    const textArea = useRef(null)
    const fileInputRef = useRef(null);
    const [fileAttachment, setFileAttachment] = useState([]);
    const [fileAllData, setFileAllData] = useState([]);
    const [loader, setLoader] = useState(false)
    const { userProfile, commentSidebar, alttextEnabled, fullData } = useAppContextProvider();
    const [selectedFiles, setSelectedFiles] = useState([]); // Initialize as null or an object
    const [attachmentTypes, setAttachmentTypes] = useState('');
    const [altTextGeneration, setAltTextGeneration] = useState(''); // 'yes' | 'no' | ''
    const [imageAction, setImageAction] = useState(''); // 'new' | 'replace' | ''
    const [replaceImageName, setReplaceImageName] = useState('');
    const [mediaAction, setMediaAction] = useState(''); // 'new' | 'replace' | ''
    const [replaceMediaName, setReplaceMediaName] = useState('');
    const [imageFileList, setImageFileList] = useState([]);
    const [supplementaryFileList, setSupplementaryFileList] = useState([]);
    const [replaceDropdownOpen, setReplaceDropdownOpen] = useState(false);
    const replaceDropdownRef = useRef(null);
    const [imageTypeDropdownOpen, setImageTypeDropdownOpen] = useState(false);
    const imageTypeDropdownRef = useRef(null);
    const [isFileUploaded, setIsFileUploaded] = useState(false); // New state to track upload status
    const [replyCategory, setReplyCategory] = useState(''); // Master Copier reply category: 'Art' | 'Comp'
    useEffect(() => {
        if (initialCategory !== undefined) {
            setReplyCategory(initialCategory || '');
        }
    }, [initialCategory]);

    // Fetch the list of images from the Image tab's folder so the "Replace Image"
    // dropdown can offer the exact existing image names to attach against.
    useEffect(() => {
        if (!fullData?.Content) return;
        const regex = /(?<!.)\\graphicspath\{\{([^}]*)\}\}/;
        const match = fullData.Content.match(regex);
        if (!match) return;
        const folderPath = match[1].replace(/\/$/, "");

        const getImageFileList = async () => {
            try {
                const url = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].get_image_details}`;
                const response = await axios.post(url, { file_path: folderPath });
                setImageFileList(response.data.Files || []);
            } catch (error) {
                console.log(error);
            }
        };
        getImageFileList();
    }, [fullData]);

    // Fetch the list of Supplementary tab files so the "Replace File" dropdown
    // (Media attachments) can offer the exact existing file names to attach against.
    useEffect(() => {
        if (!userProfile?.docid) return;

        const getSupplementaryFileList = async () => {
            try {
                const GetDetailDataString = localStorage.getItem(`${userProfile.docid}_init-getdetails`);
                const GetDetailData = JSON.parse(GetDetailDataString);
                if (!GetDetailData?.Articledetails?.ArtResourcePath) return;

                const basePath = `${GetDetailData.Articledetails.ArtResourcePath}/Supplementary`;
                const url = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].retrievefiles}`;
                const response = await axios.post(url, { InputPath: basePath });

                const filteredFiles = (response.data || [])
                    .filter((f) => !f.path.replace(basePath + "/", "").includes("/"))
                    .map((f) => ({ name: f.path.split('/').pop() }));

                setSupplementaryFileList(filteredFiles);
            } catch (error) {
                console.log(error);
            }
        };
        getSupplementaryFileList();
    }, [userProfile]);

    // Close the "Select Image/File to Replace" custom dropdown on outside click or Escape
    useEffect(() => {
        if (!replaceDropdownOpen) return;
        const handleClickOutside = (e) => {
            if (replaceDropdownRef.current && !replaceDropdownRef.current.contains(e.target)) {
                setReplaceDropdownOpen(false);
            }
        };
        const handleEsc = (e) => {
            if (e.key === 'Escape') setReplaceDropdownOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [replaceDropdownOpen]);

    // Close the "Image Type" custom dropdown on outside click or Escape
    useEffect(() => {
        if (!imageTypeDropdownOpen) return;
        const handleClickOutside = (e) => {
            if (imageTypeDropdownRef.current && !imageTypeDropdownRef.current.contains(e.target)) {
                setImageTypeDropdownOpen(false);
            }
        };
        const handleEsc = (e) => {
            if (e.key === 'Escape') setImageTypeDropdownOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [imageTypeDropdownOpen]);


    // const handleSave = async () => {
    //     const commentText = textArea.current.innerHTML.trim();

    //     // Validate comment input
    //     if (!commentText) {
    //         toastError('Please enter valuable comments; the comment is currently empty.');
    //         return;
    //     }

    //     let uploadedFiles = [...fileAttachment]; // Preserve existing file attachments
    //     let uploadedFileArray;
    //     // Handle file uploads if files are selected
    //     if (selectedFiles.length > 0) {

    //         for (const file of selectedFiles) {
    //             if (!attachmentTypes) {
    //                 toastError(`Please select an attachment type for ${file.name}`);
    //                 return;
    //             }
    //             fileProgress(true);
    //             uploadedFileArray = await handleFileupload(file.file);
    //             const uploadedFile = Array.isArray(uploadedFileArray) ? uploadedFileArray[0] : uploadedFileArray;
    //             if (uploadedFile) uploadedFiles.push(uploadedFile);
    //         }
    //     }

    //     // Wait for fileAttachment state to update
    //     // setFileAttachment(uploadedFiles);
    //     if (uploadedFileArray !== null) {
    //         // Ensure state is updated before calling onSaveComment
    //         onSaveComment({
    //             comment: commentText,
    //             fileattached: uploadedFiles.length ? uploadedFiles : commentAttach,
    //         });

    //         // Clear states after saving
    //         textArea.current.innerHTML = '';
    //         setSelectedFiles([]);
    //         setAttachmentTypes({});
    //         setFileAttachment([]);
    //         setIsFileUploaded(false);
    //         if (uploadedFiles.length !== 0) {
    //         fileProgress(false);
    //         }
    //         // Small delay ensures state is updated
    //     } else {
    //         fileProgress(false);
    //     }
    // };


    const handleSave = async () => {
        // First check if text is selected
        if (!isReply && (!commentSidebar?.selectValue.selectText || commentSidebar.selectValue.selectText === '')) {
            toastError('Please select a word or text to insert a comment.');
            return;
        }
    
        const commentText = textArea.current.innerHTML.trim();
        // Enhanced cleaning to handle &nbsp; and other HTML entities
        const cleanedComment = commentText
            .replace(/<[^>]*>/g, '')  // Remove HTML tags
            .replace(/&nbsp;/g, ' ')  // Convert &nbsp; to regular spaces
            .replace(/\s+/g, ' ')     // Normalize multiple spaces to single space
            .trim();                  // Remove leading/trailing spaces
        
        // Strict validation for comment content
        if (!cleanedComment) {
            toastError('Please enter valuable comments; empty comments are not allowed.');
            return;
        }
    
        let uploadedFiles = [...fileAttachment];
        let uploadedFileArray;
    
        if (selectedFiles.length > 0) {
            for (const file of selectedFiles) {
                if (!attachmentTypes ||
                    attachmentTypes === '' ||
                    (typeof attachmentTypes === 'object' && Object.keys(attachmentTypes).length === 0)) {
                    toastError(`Please select an attachment type for ${file.name}`);
                    return;
                }
                if (attachmentTypes === 'mediaAttachment' && !isValidMediaFile(file.name)) {
                    toastError(`"${file.name}" is not a valid Media file. Allowed formats: ${ALLOWED_MEDIA_EXTENSIONS.map(ext => '.' + ext).join(', ')}`);
                    return;
                }
                if (attachmentTypes === 'imageAttachment' && !isValidImageFile(file.name)) {
                    toastError(`"${file.name}" is not a valid Image file. Allowed formats: ${ALLOWED_IMAGE_EXTENSIONS.map(ext => '.' + ext).join(', ')}`);
                    return;
                }
                if (alttextEnabled && attachmentTypes === 'imageAttachment' && !altTextGeneration) {
                    toastError(`Please select an alternative text option for ${file.name}`);
                    return;
                }
                if (alttextEnabled && attachmentTypes === 'imageAttachment' && !imageAction) {
                    toastError(`Please select an image type for ${file.name}`);
                    return;
                }
                if (alttextEnabled && attachmentTypes === 'imageAttachment' && imageAction === 'replace' && !replaceImageName) {
                    toastError(`Please select the image to replace for ${file.name}`);
                    return;
                }
                if (alttextEnabled && attachmentTypes === 'mediaAttachment' && !mediaAction) {
                    toastError(`Please select a file type for ${file.name}`);
                    return;
                }
                if (alttextEnabled && attachmentTypes === 'mediaAttachment' && mediaAction === 'replace' && !replaceMediaName) {
                    toastError(`Please select the file to replace for ${file.name}`);
                    return;
                }
                fileProgress(true);
                uploadedFileArray = await handleFileupload(file.file);
                const uploadedFile = Array.isArray(uploadedFileArray) ? uploadedFileArray[0] : uploadedFileArray;
                if (uploadedFile) uploadedFiles.push(uploadedFile);
            }
        }
    
        if (uploadedFileArray !== null) {
            onSaveComment({
                comment: commentText,
                fileattached: uploadedFiles.length ? uploadedFiles : commentAttach,
                category: (showCategory && userProfile.role === 'Master Copier') ? replyCategory : undefined,
            });
    
            textArea.current.innerHTML = '';
            setSelectedFiles([]);
            setAttachmentTypes({});
            setAltTextGeneration('');
            setImageAction('');
            setReplaceImageName('');
            setMediaAction('');
            setReplaceMediaName('');
            setReplyCategory('');
            setFileAttachment([]);
            setIsFileUploaded(false);
            if (uploadedFiles.length !== 0) {
                fileProgress(false);
            }
        } else {
            fileProgress(false);
        }
    };
    

    useEffect(() => {
        let docId = userProfile.docid;
        getDetails();
    }, [userProfile])

    useEffect(() => {
        const handleKeyDown = (e) => {
            // Ctrl + = for subscript
            if ((e.ctrlKey || e.metaKey) && e.key === '=') {
                e.preventDefault();
                applyStyle('subscript');
            }
            // Ctrl + Shift + + for superscript 
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === '+') {
                e.preventDefault();
                applyStyle('superscript');
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);


    const applyStyle = (command) => {
        // const currentSelection = window.getSelection();
        // if (currentSelection.rangeCount > 0) {
        //     const range = currentSelection.getRangeAt(0);
        //     const parentElement = range.commonAncestorContainer.parentNode;
        //     if (command === 'superscript' || command === 'subscript') {
        //         if (parentElement.tagName.toLowerCase() === (command === 'superscript' ? 'sup' : 'sub')) {
        //             const grandparent = parentElement.parentNode;
        //             while (parentElement.firstChild) {
        //                 grandparent.insertBefore(parentElement.firstChild, parentElement);
        //             }
        //             grandparent.removeChild(parentElement);
        //         } else {
        //             document.execCommand(command, false, null);
        //         }
        //     } else {
        //         document.execCommand(command, false, null);
        //     }
        // }

        // updated the code for the issue in the superscript and subscript toggling
        document.execCommand(command, false, null);
    };

    const handleCancel = () => {
        // Clear the text area content
        if (textArea.current) {
            textArea.current.innerHTML = '';
        }
        // Clear file attachments
        setFileAttachment([]);
        setSelectedFiles([]);
        setAttachmentTypes('');
        setAltTextGeneration('');
        setImageAction('');
        setReplaceImageName('');
        setMediaAction('');
        setReplaceMediaName('');
        setReplyCategory('');
        setIsFileUploaded(false);
        // Call the parent's cancel handler
        onCancelComment();
        setSelectedFiles([]);
        setIsFileUploaded(false);
    };

    const handleUpload = (e) => {
        const file = e.target.files[0];
        const maxFileSize = 40 * 1024 * 1024;

        if (!file) {
            return; // Exit if no file is selected
        }

        if (file.size > maxFileSize) {
            toastError('The file size should not exceed more than 40 MB');
            e.target.value = null;
            setIsFileUploaded(false);
            return;
        }

        setSelectedFiles([...selectedFiles, {
            file: file,
            name: file.name,
            size: (file.size / 1024).toFixed(2) + ' KB'
        }]);
        setIsFileUploaded(true); // Mark file as uploaded
        e.target.value = null;
    };

    const handleFileupload = async (filename) => {
        setLoader(true);

        try {
            // Prevent duplicate file uploads

            if (fileAllData.some((item) => item.Filename === filename.name)) {
                toastError(`${filename.name} is already uploaded. Please rename and retry.`);
                setLoader(false); return null; // Return null to indicate failure
            }

            // Retrieve stored job details
            const GetDetailDataString = localStorage.getItem(`${userProfile?.docid}_init-getdetails`);
            const GetDetailData = JSON.parse(GetDetailDataString || "{}");
            const fileComment = textArea.current.innerHTML.trim();

            // Prepare form data for file upload
            const formdata = new FormData();
            formdata.append("jobGUID", GetDetailData?.Articledetails?.ArticleGUID || "");
            formdata.append("ActivityID", GetDetailData?.ActivtyDetails?.ActivityID || "");
            formdata.append("docid", userProfile?.docid || "");
            formdata.append("type", "commentAttachment");
            formdata.append("files", filename);
            formdata.append("fileTypes[0]", attachmentTypes);
            formdata.append("fileComments[0]", fileComment);
            formdata.append("altTextGeneration[0]", alttextEnabled && attachmentTypes === "imageAttachment" ? altTextGeneration : null);
            formdata.append("imageAction[0]", attachmentTypes === "imageAttachment" ? imageAction : null);
            formdata.append("replaceImageName[0]", attachmentTypes === "imageAttachment" && imageAction === "replace" ? replaceImageName : null);
            formdata.append("mediaAction[0]", attachmentTypes === "mediaAttachment" ? mediaAction : null);
            formdata.append("replaceMediaName[0]", attachmentTypes === "mediaAttachment" && mediaAction === "replace" ? replaceMediaName : null);

            // API endpoint for file upload
            const url = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].upload_attachment}`;

            // Perform the file upload request
            const response = await axios.post(url, formdata, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (response?.data?.SuccessFiles?.length) {
                response.data.SuccessFiles.forEach((item) =>
                    toastSuccess(`${item.data[0].Filename} - ${item.message}`)
                );

                // Await `getDetails(filename)` to ensure it completes before proceeding
                const updatedFiles = await getDetails(filename);
                console.log("Updated file details:", updatedFiles);

                getTriggerfile(true); // Trigger file update
                refreshAttachments();
                setLoader(false);
                return updatedFiles?.[0] || null; // Return updated file data
            }
            console.log(response, 'response');
            if (response?.data?.FailedFiles?.length) {
                response.data.FailedFiles.forEach((item) =>
                    toastError(`${item.data[0].Filename} - ${item.message}`)
                );
            }

            setLoader(false);
            return null; // Return null if upload failed
        } catch (error) {
            console.error("File upload error:", error);
            toastError(error.message || "File upload failed.");
            setLoader(false);
            return null; // Ensure function always returns a value
        }
    };



    const getDetails = async (filename) => {
        try {
            const GetDetailDataString = localStorage.getItem(`${userProfile?.docid}_init-getdetails`);
            if (!GetDetailDataString) throw new Error("No stored details found.");

            const GetDetailData = JSON.parse(GetDetailDataString);
            if (!GetDetailData?.jobdetails) throw new Error("Invalid or missing job details.");

            const getUrl = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].getAttachment}`;

            const body = {
                Proof_Type: GetDetailData.jobdetails.ProofType,
                JobGUID: GetDetailData.jobdetails.JobGUID,
                docid: userProfile?.docid,
            };

            const response = await axios.post(getUrl, body);
            if (!response.data?.data) throw new Error("Invalid response data format.");

            const dataWithFileType = response.data.data.map((item) => ({
                ...item,
                filetype: item.Filename?.split('.').pop() || "unknown",
            }));
            const commentAttachments = dataWithFileType.filter(item => item.AttachType === 'imageAttachment' || item.AttachType === 'textAttachment' || item.AttachType === 'mediaAttachment' || item.AttachType === 'otherAttachment');
            if (filename) {
                const matchedFiles = commentAttachments.filter(item => item.Filename === filename.name);
                setFileAttachment(matchedFiles);
                return matchedFiles;
            } else {
                console.log(commentAttachments, 'commentAttachments')
                setFileAllData(commentAttachments);
                return commentAttachments;
            }
        } catch (error) {
            console.error("Error in getDetails:", error);
            toastError(error.message);
            return null;
        } finally {
            setLoader(false);
        }
    };



    const deleteConfirm = (value) => {
        let GetDetailDataString = localStorage.getItem(`${userProfile?.docid}_init-getdetails`);
        let GetDetailData = JSON.parse(GetDetailDataString);
        let data = {
            "ArticleGUID": value.ArticleGUID,
            "AttachID": value.AttachID,
            "Proof_Type": GetDetailData.jobdetails.ProofType,
            "AttachFlag": value.AttachFlag,
            "docid": userProfile?.docid
        }
        const deleteUrl = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].deleteAttachment}`
        axios.post(deleteUrl, data).then((response) => {
            toastSuccess(`${response.data.data[0].Filename} - ${response.data.message}`)
            getDetails()
            setFileAttachment([])
        }
        ).catch((error) => {
            console.log(error)
            toastError(error.message);
        })
    }

    useEffect(() => {
        console.log("Initial content:", initialContent, commentAttach);
        if (initialContent && textArea.current) {
            textArea.current.innerHTML = initialContent;
        }
    }, [initialContent]);


    return (
        <div className="comment-box">
            <div className="reply-btns">
                <div>
                    <Button onClick={() => applyStyle('bold')} title="Bold (Ctrl+B)">
                        <FaBold />
                    </Button>
                    <Button onClick={() => applyStyle('italic')} title='Italic'>
                        <FaItalic />
                    </Button>
                    <Button onClick={() => applyStyle('underline')} title='Underline'>
                        <FaUnderline />
                    </Button>
                    <Button onClick={() => applyStyle('superscript')} title='SuperScript'>
                        <FaSuperscript />
                    </Button>
                    <Button onClick={() => applyStyle('subscript')} title='SubScript'>
                        <FaSubscript />
                    </Button>
                    {/* Added for Accessblity test */}
                   <Button className="attchcomment-btn" title="Attachment">
                    <FaPaperclip />
                    <input
                        type="file"
                        aria-label="Attachment"
                        onChange={(e) => handleUpload(e)}
                        ref={fileInputRef}
                        style={{
                        position: 'absolute',
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer'
                        }}
                    />
                    </Button>

                </div>
                {showCategory && userProfile.role === 'Master Copier' && (
                    <select
                        id="reply-category-select"
                        aria-label="Reply category selection"
                        title="Category"
                        className="reply-category-dropdown"
                        value={replyCategory}
                        onChange={(e) => setReplyCategory(e.target.value)}
                        required
                    >
                        <option value="">Select</option>
                        <option value="Art">Art</option>
                        <option value="Comp">Comp</option>
                    </select>
                )}
                {/* <div>
                    <Button onClick={handleCancel}><FaTimes /></Button>
                </div> */}
            </div>
            <div className="CommentContent">
                <div contentEditable="true" id="TypeContent" className="CommentTextEntry UIFont Cmdtxtcontent" ref={textArea}></div>
            </div>
            {/* {selectedFiles && (
                <select
                    onChange={(e) => setAttachmentTypes(e.target.value)}
                    required
                    className="attachment-type-dropdown"
                >
                    <option value="">Select Type</option>
                    <option value="image">Image</option>
                    <option value="text">Text</option>
                </select>
            )} */}
            {/* {fileAttachment.length !== 0 ? <div className='comment-attached'>
                {fileAttachment.map((item, index) =>
                    <div className='comment-attached-list' key={index}>
                        <p>{item.Filename}</p>
                        <label>size - {item.Filesize}</label>
                        <button onClick={() => deleteConfirm(item)}><FaRegTrashAlt /></button>
                    </div>
                )}
            </div> : loader ? <div className='fileupload-loder'><span className="loader"></span></div> : ''
            } */}
            {/* File details section */}
            {/* {selectedFiles && (
                <div className='comment-attached'>
                    <div className='comment-attached-list'>
                        <p>{selectedFiles.name}</p>
                        <label>size - {selectedFiles.size}</label>
                        <button onClick={() => {
                            setSelectedFiles(null);
                            setAttachmentTypes('');
                        }}>
                            <FaRegTrashAlt />
                        </button>
                    </div>
                </div>
            )} */}
            {isFileUploaded && selectedFiles && (
                <>
                    <select
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === 'mediaAttachment') {
                                const invalidFile = selectedFiles.find((f) => !isValidMediaFile(f.name));
                                if (invalidFile) {
                                    toastError(`"${invalidFile.name}" is not a valid Media file. Allowed formats: ${ALLOWED_MEDIA_EXTENSIONS.map(ext => '.' + ext).join(', ')}`);
                                    return;
                                }
                            }
                            if (value === 'imageAttachment') {
                                const invalidFile = selectedFiles.find((f) => !isValidImageFile(f.name));
                                if (invalidFile) {
                                    toastError(`"${invalidFile.name}" is not a valid Image file. Allowed formats: ${ALLOWED_IMAGE_EXTENSIONS.map(ext => '.' + ext).join(', ')}`);
                                    return;
                                }
                            }
                            setAttachmentTypes(value);
                            setAltTextGeneration(''); // reset alt-text when type changes
                            setImageAction('');
                            setReplaceImageName('');
                            setMediaAction('');
                            setReplaceMediaName('');
                        }}
                        required
                        className="attachment-type-dropdown"
                        aria-label='Attachment type selection for type of file'
                    >
                        <option value="">Select Type</option>
                        <option value="imageAttachment">Image</option>
                        <option value="textAttachment">Text</option>
                        <option value="mediaAttachment">Media</option>
                        <option value="otherAttachment">Other</option>
                    </select>

                    {/* New Image / Replace Image choice (only when alttextEnabled).
                        Custom listbox (not a native <select>) so the labels can be bold. */}
                    {alttextEnabled && attachmentTypes === 'imageAttachment' && (() => {
                        const imageActionOptions = [
                            { value: 'new', label: 'New Image', description: 'Adding a new figure entirely (never existed before)' },
                            { value: 'replace', label: 'Replace Image', description: "Swapping an existing figure's file (updates a current one)" },
                        ];
                        const currentOption = imageActionOptions.find(opt => opt.value === imageAction);
                        return (
                            <div className="att-form-group" style={{ marginTop: '8px' }}>
                                <div className="att-custom-select" ref={imageTypeDropdownOpen ? imageTypeDropdownRef : null}>
                                    <button
                                        type="button"
                                        className="att-custom-select-trigger"
                                        aria-label="Image type selection"
                                        onClick={() => setImageTypeDropdownOpen((prev) => !prev)}
                                    >
                                        <span>
                                            {currentOption ? (
                                                <><strong>{currentOption.label}</strong> – {currentOption.description}</>
                                            ) : '-- Choose an option --'}
                                        </span>
                                        <FaChevronDown className="att-custom-select-arrow" />
                                    </button>
                                    {imageTypeDropdownOpen && (
                                        <ul className="att-custom-select-list" role="listbox">
                                            {imageActionOptions.map((opt) => {
                                                const isSelected = imageAction === opt.value;
                                                return (
                                                    <li
                                                        key={opt.value}
                                                        role="option"
                                                        aria-selected={isSelected}
                                                        tabIndex={0}
                                                        className={`att-custom-select-option ${isSelected ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setImageAction(opt.value);
                                                            setReplaceImageName('');
                                                            setImageTypeDropdownOpen(false);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                setImageAction(opt.value);
                                                                setReplaceImageName('');
                                                                setImageTypeDropdownOpen(false);
                                                            }
                                                        }}
                                                    >
                                                        <strong>{opt.label}</strong> – {opt.description}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Existing image name picker, only when alttextEnabled and "Replace Image" is chosen */}
                    {alttextEnabled && attachmentTypes === 'imageAttachment' && imageAction === 'replace' && (
                        <div className="att-form-group" style={{ marginTop: '8px' }}>
                            <div className="att-custom-select" ref={replaceDropdownOpen ? replaceDropdownRef : null}>
                                <button
                                    type="button"
                                    className="att-custom-select-trigger"
                                    onClick={() => setReplaceDropdownOpen((prev) => !prev)}
                                >
                                    <span>{getImageDisplayTitle(replaceImageName) || '-- Select image --'}</span>
                                    <FaChevronDown className="att-custom-select-arrow" />
                                </button>
                                {replaceDropdownOpen && (
                                    <ul className="att-custom-select-list" role="listbox">
                                        {[...imageFileList]
                                            .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
                                            .map((img, imgIndex) => (
                                                <li
                                                    key={imgIndex}
                                                    role="option"
                                                    aria-selected={replaceImageName === img.name}
                                                    tabIndex={0}
                                                    className={`att-custom-select-option ${replaceImageName === img.name ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        setReplaceImageName(img.name);
                                                        setReplaceDropdownOpen(false);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            setReplaceImageName(img.name);
                                                            setReplaceDropdownOpen(false);
                                                        }
                                                    }}
                                                >
                                                    {getImageDisplayTitle(img.name)}
                                                </li>
                                            ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}

                    {/* New File / Replace File choice (only when alttextEnabled) */}
                    {alttextEnabled && attachmentTypes === 'mediaAttachment' && (
                        <div className="att-form-group" style={{ marginTop: '8px' }}>
                            <select
                                value={mediaAction}
                                onChange={(e) => {
                                    setMediaAction(e.target.value);
                                    setReplaceMediaName('');
                                }}
                                required
                                className="att-select"
                                aria-label="File type selection"
                            >
                                <option value="">-- Choose an option --</option>
                                <option value="new">New File</option>
                                <option value="replace">Replace File</option>
                            </select>
                        </div>
                    )}

                    {/* Existing supplementary file name picker, only when alttextEnabled and "Replace File" is chosen */}
                    {alttextEnabled && attachmentTypes === 'mediaAttachment' && mediaAction === 'replace' && (
                        <div className="att-form-group" style={{ marginTop: '8px' }}>
                            <div className="att-custom-select" ref={replaceDropdownOpen ? replaceDropdownRef : null}>
                                <button
                                    type="button"
                                    className="att-custom-select-trigger"
                                    onClick={() => setReplaceDropdownOpen((prev) => !prev)}
                                >
                                    <span>{replaceMediaName || '-- Select file --'}</span>
                                    <FaChevronDown className="att-custom-select-arrow" />
                                </button>
                                {replaceDropdownOpen && (
                                    <ul className="att-custom-select-list" role="listbox">
                                        {[...supplementaryFileList]
                                            .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
                                            .map((suppFile, suppIndex) => (
                                                <li
                                                    key={suppIndex}
                                                    role="option"
                                                    aria-selected={replaceMediaName === suppFile.name}
                                                    tabIndex={0}
                                                    className={`att-custom-select-option ${replaceMediaName === suppFile.name ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        setReplaceMediaName(suppFile.name);
                                                        setReplaceDropdownOpen(false);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            setReplaceMediaName(suppFile.name);
                                                            setReplaceDropdownOpen(false);
                                                        }
                                                    }}
                                                >
                                                    {suppFile.name}
                                                </li>
                                            ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Alt-text option — shown only for image attachments when feature is enabled */}
                    {alttextEnabled && attachmentTypes === 'imageAttachment' && (
                        <div className="att-form-group" style={{ marginTop: '8px' }}>
                            <div className="att-alt-section">
                                <div className="att-alt-header">
                                    Alternative text option <span className="att-required">*</span>
                                </div>
                                <div className="att-alt-body">
                                    <label className={`att-alt-option ${altTextGeneration === 'yes' ? 'att-selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="comment-alt-text"
                                            value="yes"
                                            checked={altTextGeneration === 'yes'}
                                            onChange={() => setAltTextGeneration('yes')}
                                            required
                                        />
                                        <div className="att-alt-option-text">
                                            <strong>Add/replace with a completely new image</strong>
                                            <span>A new alt-text needs to be generated.</span>
                                        </div>
                                    </label>

                                    <label className={`att-alt-option ${altTextGeneration === 'no' ? 'att-selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="comment-alt-text"
                                            value="no"
                                            checked={altTextGeneration === 'no'}
                                            onChange={() => setAltTextGeneration('no')}
                                        />
                                        <div className="att-alt-option-text">
                                            <strong>Upload the same image in higher resolution</strong>
                                            <span>Retain the previously generated alt-text</span>
                                        </div>
                                    </label>

                                    <div className="att-info-banner">
                                        <svg className="att-info-icon" width="14" height="14" viewBox="0 0 16 16" fill="none">
                                            <path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm9-3a1 1 0 11-2 0 1 1 0 012 0zM6.75 7.75a.75.75 0 000 1.5h.5V11a.75.75 0 001.5 0V8.5a.75.75 0 00-.75-.75h-1.25z" fill="#0c5aa8" />
                                        </svg>
                                        <span>If only the resolution changed (low to high), new alt text may not be required.</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="comment-attached">
                        {selectedFiles.map((item, index) =>
                            <div className="comment-attached-list" key={index} aria-label='file attachment with comment'>
                                <p>{item.name}</p> {/* Accessing object properties */}
                                <label>size - {item.size}</label>
                                <button aria-label='Remove attachment'
                                    onClick={() => {
                                        setSelectedFiles([]); // Reset selected file
                                        setAttachmentTypes(''); // Clear dropdown
                                        setAltTextGeneration(''); // Clear alt-text selection
                                        setIsFileUploaded(false); // Reset upload state
                                    }}
                                >
                                    <FaRegTrashAlt />
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}


            <div className="comment-footer">
                <Button onClick={handleSave} title="Save" className="save-btn-fixedContrast"><FaSave /> Save</Button>
                <Button onClick={handleCancel} title="Cancle" className="cancel-btn-fixedContrast"><FaTimes /> Cancel</Button>
            </div>
        </div >
    );
};

export default CommentBox;